import { useState, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useClerk, useUser } from '../../lib/auth';
import { useRBAC } from '../../contexts/RBACContext';
import { IS_DEMO_BUILD } from '../../lib/demoMode';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useBranding } from '../../contexts/BrandingContext';
import Icon from '../ui/Icon';
import {
  NAV_GROUPS,
  isSubGroup,
  gateIsVisible,
  itemIsVisible,
  subGroupIsVisible,
  entryContainsPath,
  withDemoSession,
} from './sidebarNav';
import type { Role, NavEntry, NavGroupDef, NavSubGroupDef } from './sidebarNav';

interface SidebarProps {
  isOpen?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

// ─── Role badge config ────────────────────────────────────────
const ROLE_BADGES: Record<string, { label: string; color: string; bg: string }> = {
  ceo:         { label: 'CEO',         color: 'var(--brand)', bg: '#dbeafe' },
  admin:       { label: 'Admin',       color: '#6b21a8', bg: '#f3e8ff' },
  manager:     { label: 'Manager',     color: '#0f766e', bg: '#ccfbf1' },
  hr:          { label: 'HR',          color: 'var(--warn-fg)', bg: 'var(--warn-tint)' },
  recruiter:   { label: 'Recruiter',   color: 'var(--info-fg)', bg: 'var(--info-tint)' },
  coordinator: { label: 'Coordinator', color: 'var(--ai)', bg: 'var(--ai-tint)' },
  viewer:      { label: 'Viewer',      color: 'var(--text-muted)', bg: 'var(--surface-subtle)' },
  va:          { label: 'VA',          color: '#9d174d', bg: '#fce7f3' },
};

// ─── SidebarSubGroup ──────────────────────────────────────────
// The nested-dropdown level, one indent deeper than a top-level group.
// Styled with Tailwind (new UI in this app goes through Tailwind; the rest
// of this file's existing chrome — .sb-item, .sb-group-btn, etc. — stays on
// the plain-CSS classes it already had).
function SidebarSubGroup({ sub, role, has }: { sub: NavSubGroupDef; role: Role; has: (k: string) => boolean }) {
  const location = useLocation();
  const { applyTerm } = useBranding();
  const visibleItems = useMemo(
    () => sub.items.filter((item) => itemIsVisible(item, role, has)),
    [sub, role, has]
  );
  // See entryContainsPath's comment — mount-time-only sync, never fights a
  // manual collapse during the session.
  const [open, setOpen] = useState(
    () => (sub.defaultOpen ?? false) || visibleItems.some((i) => i.to === location.pathname)
  );
  if (visibleItems.length === 0) return null;

  return (
    <div>
      {/* Deliberately mirrors .sb-group-btn/.sb-group-label's recipe (10px,
          bold, uppercase, letter-spaced, muted) rather than inventing a new
          look — a first pass styled this as a 13px sentence-case semibold
          row, which read as an unrelated component bolted onto the sidebar
          instead of a nested continuation of the same section-header
          language already established one level up. */}
      {/* bg-transparent + border-none reset the browser's native <button>
          chrome — a real <button> ships its own UA-stylesheet background
          (ButtonFace, a gray that differs between light and dark OS
          appearance) and border. Every other custom button in this file
          resets that either via the .sb-item/.sb-group-btn CSS classes
          (background: none) or an inline style; this one just never got it,
          so it rendered with a visible gray box unrelated to the sidebar's
          own theme — the reported "different background color". */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-1.5 border-none bg-transparent py-2 pl-5 pr-4
                   text-left text-[10px] font-bold uppercase tracking-[0.08em] text-muted
                   transition-colors hover:text-strong"
      >
        <Icon
          name="chevron-right"
          size={10}
          className={`shrink-0 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
        />
        <span className="flex-1">{applyTerm(sub.label)}</span>
      </button>

      {open && (
        // The indent guide (border-l) is what visually distinguishes a
        // nested item from a top-level one — the links themselves reuse the
        // plain .sb-item class so their hover/active/dark-mode styling is
        // byte-identical to every other nav row, not a re-implementation.
        <div className="ml-5 border-l border-border pl-2">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) => `sb-item${isActive ? ' active' : ''}`}
            >
              <span className="sb-item-icon">
                <Icon name={item.icon} size={16} />
              </span>
              <span className="sb-item-label">{applyTerm(item.label)}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SidebarGroup ─────────────────────────────────────────────
function SidebarGroup({ group, role, has }: { group: NavGroupDef; role: Role; has: (k: string) => boolean }) {
  const location = useLocation();
  // Tenant terminology (white-label): relabels entity nouns in nav text.
  const { applyTerm } = useBranding();
  // Memoized on the permission inputs so this whole filter pass doesn't
  // re-run on every unrelated render (this component is on every page).
  const cleaned = useMemo<NavEntry[]>(
    () => group.items.filter((entry) =>
      isSubGroup(entry) ? subGroupIsVisible(entry, role, has) : itemIsVisible(entry, role, has)
    ),
    [group, role, has]
  );
  const [open, setOpen] = useState(
    () => (group.defaultOpen ?? false) || entryContainsPath(cleaned, location.pathname)
  );
  // Count leaf links, descending into subgroups, toward "is this group worth
  // showing?" — a group whose only content is one now-empty subgroup
  // shouldn't render as a header with nothing under it.
  const linkCount = cleaned.reduce(
    (n, e) => n + (isSubGroup(e) ? e.items.filter((i) => itemIsVisible(i, role, has)).length : 1),
    0
  );
  if (linkCount === 0) return null;

  return (
    <div className="sb-group">
      <button
        className="sb-group-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        type="button"
      >
        <span className="sb-group-label">{applyTerm(group.title)}</span>
        <span className={`sb-group-chevron${open ? ' open' : ''}`}><Icon name="chevron-down" size={11} /></span>
      </button>

      {open && cleaned.map((entry, idx) => (
        isSubGroup(entry) ? (
          <SidebarSubGroup key={`sg-${idx}-${entry.label}`} sub={entry} role={role} has={has} />
        ) : (
          <NavLink
            key={entry.to}
            to={entry.to}
            // Exact match only. Without `end`, React Router's default NavLink
            // matching treats `to` as a path PREFIX — so viewing
            // /credentialing/compliance-monitor also lit up "Records" (whose
            // `to` is /credentialing, a prefix of that path), and viewing
            // /compliance/admin/records also lit up "Overview" (`to` is the
            // parent /compliance/admin). Every nav entry here is a distinct
            // leaf page, not a parent section, so exact matching is correct
            // for all of them — there is no case where prefix matching was
            // the intended behavior.
            end
            className={({ isActive }) => `sb-item${isActive ? ' active' : ''}`}
          >
            <span className="sb-item-icon">
              <Icon name={entry.icon} size={17} />
            </span>
            <span className="sb-item-label">{applyTerm(entry.label)}</span>
          </NavLink>
        )
      ))}
    </div>
  );
}

// ─── Main Sidebar ─────────────────────────────────────────────
export default function Sidebar({ isOpen = true, isMobile = false, onClose }: SidebarProps) {
  const { signOut } = useClerk();
  const { user } = useUser();
  const { role } = useRBAC();
  const { has: permHas, simulatedRole } = usePermissions();
  // A demo session on "Full access" also sees the demo-capable platform items
  // (the sandboxed Staff Console) — see withDemoSession in sidebarNav.ts.
  const has = useMemo(() => withDemoSession(permHas, IS_DEMO_BUILD && !simulatedRole), [permHas, simulatedRole]);
  const navigate = useNavigate();

  const fullName =
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.primaryEmailAddress?.emailAddress ||
    'User';

  const initials = fullName
    .split(' ')
    .map((w: string) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const badge = role ? ROLE_BADGES[role] : ROLE_BADGES['viewer'];
  // Memoized so the ~100 permission checks across all nav groups don't re-run on
  // every navigation / provider render. Holds now that role + has are stable.
  const visibleGroups = useMemo(() => NAV_GROUPS.filter((g) => gateIsVisible(g, role, has)), [role, has]);

  return (
    <nav
      className="sidebar"
      aria-label="Main navigation"
      style={isMobile ? {
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 200,
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s ease',
        boxShadow: isOpen ? '4px 0 20px rgba(0,0,0,0.15)' : 'none',
      } : {
        // Desktop: when collapsed, slide the sidebar fully off-screen
        // and zero out its width so the main panel reclaims the space.
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        width: isOpen ? undefined : 0,
        minWidth: isOpen ? undefined : 0,
        overflow: isOpen ? undefined : 'hidden',
        transition: 'transform 0.2s ease, width 0.2s ease',
      }}
    >
      {/* Mobile close button */}
      {isMobile && (
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, color: '#fff', fontSize: 18, cursor: 'pointer', padding: '4px 8px', lineHeight: 1, zIndex: 10 }}
          aria-label="Close menu"
        >
          <Icon name="x" size={14} />
        </button>
      )}

      {/* Brand intentionally NOT repeated here. The top bar already renders
          the same tenant logo + app_name from the same `branding` source
          directly above this column, so a second copy was pure duplication
          and cost the nav a row of vertical space. Single source: Topbar's
          .topbar-logo. */}

      {/* User card */}
      <div
        className="sb-user"
        onClick={() => navigate('/settings/users')}
        title="Go to settings"
        role="button"
      >
        <div className="sb-avatar">{initials}</div>
        <div className="sb-user-info">
          <div className="sb-user-name">{fullName}</div>
          {badge && (
            <span className="sb-role-badge" style={{ color: badge.color, background: badge.bg }}>
              {badge.label}
            </span>
          )}
        </div>
      </div>

      {/* Nav groups */}
      <div className="sb-nav">
        {visibleGroups.map((group) => (
          <SidebarGroup key={group.title} group={group} role={role} has={has} />
        ))}
      </div>

      {/* Bottom */}
      <div className="sb-bottom">
        <button
          className="sb-signout"
          type="button"
          onClick={() => signOut({ redirectUrl: '/' })}
        >
          <span className="sb-item-icon"><Icon name="door" size={16} /></span>
          <span>Sign Out</span>
        </button>
      </div>
    </nav>
  );
}
